import type { EntryKey } from "@pinia/colada";
import type {
  StructuredAnalysis,
  StructuredAnalysisBody,
} from "@/schemas/structured-analysis.schema";
import { useMutation, useQuery, useQueryCache } from "@pinia/colada";
import { structuredAnalysisApi } from "@/api/structured-analysis.api";
import { SUBSCRIPTION_LIMITS_QUERY_KEYS } from "@/queries/subscription-limits.query";
import { api } from "@/services/api";
import { useProjectStore } from "@/stores/project.store";

export const STRUCTURED_ANALYSIS_QUERY_KEYS = {
  root: ["structured-analyses"] as const,
  list: (projectId?: string | null) =>
    [...STRUCTURED_ANALYSIS_QUERY_KEYS.root, "list", { projectId }] as const,
};

/**
 * Query hook for fetching structured analyses
 *
 * Features:
 * - **Project filtering**: Automatically filters by selected project via X-Project-ID header
 * - Smart caching with 5min stale time
 * - Query refetches when selected project changes
 */
export function useStructuredAnalysesQuery() {
  const projectStore = useProjectStore();

  return useQuery({
    // Include project ID in key so query refetches when project changes
    key: () =>
      STRUCTURED_ANALYSIS_QUERY_KEYS.list(projectStore.currentProjectId) as unknown as EntryKey,
    query: async () => {
      // Pass project header to filter by selected project
      const { data } = await api.get<StructuredAnalysis[]>("/structured-analyses", {
        headers: projectStore.getProjectHeader(),
      });
      return data;
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/**
 * Query hook for fetching ALL structured analyses across projects (org-level view).
 *
 * Used in agent forms where post-analysis should show all available analyses
 * regardless of the currently selected project.
 */
export function useAllStructuredAnalysesQuery() {
  return useQuery({
    key: () => [...STRUCTURED_ANALYSIS_QUERY_KEYS.root, "all"] as unknown as EntryKey,
    query: async () => {
      const { data } = await api.get<StructuredAnalysis[]>("/structured-analyses");
      return data;
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/**
 * Create new structured analysis
 *
 * Uses project header to create structured analysis in the selected project.
 */
export function useCreateStructuredAnalysis() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: StructuredAnalysisBody) => {
      return await structuredAnalysisApi.create(payload, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: STRUCTURED_ANALYSIS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Update existing structured analysis
 *
 * Uses project header for access control.
 */
export function useUpdateStructuredAnalysis() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: { id: string } & Partial<StructuredAnalysisBody>) => {
      const { id, ...body } = payload;
      return await structuredAnalysisApi.updateById(id, body, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: STRUCTURED_ANALYSIS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Delete structured analysis
 *
 * Uses project header for access control.
 */
export function useDeleteStructuredAnalysis() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (id: string) => {
      return await structuredAnalysisApi.deleteById(id, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: STRUCTURED_ANALYSIS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}
